import { useAppContext } from '../../../context/AppContext';
import { useEffect, useState } from 'react';
import confetti from 'canvas-confetti';
import { updateData, postData } from '../../../services';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner, faPen } from '@fortawesome/free-solid-svg-icons';
import Modal from '../../helpers/Modal';
const TEAM = [
    'ashley@mtsolar.us',
    'christina@mtsolar.us',
    'jamie@mtsolar.us',
    'jennifer@mtsolar.us',
    'jeremy@mtsolar.us',
    'kris@mtsolar.us',
    'mike.henderson@mtsolar.us',
    'noel@mtsolar.us',
    'developer@mtsolar.us',
    'andrew@mtsolar.us',
    'penni@mtsolar.us',
    'janelle@mtsolar.us',
    'packaging@mtsolar.us',
    'alex@mtsolar.us'
];

const TASK_STATUSES = ['Not Started', 'In Progress', 'On Hold', 'Completed'];

const isSameTask = (a, b) =>
    a.task_name === b.task_name &&
    a.assigned_to === b.assigned_to &&
    a.due_date === b.due_date;

export const EditTaskForm = ({ project, task, onProjectUpdated }) => {
    const { token, base_url, setProjects } = useAppContext();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({
        status: 'Not Started',
        assigned_to: '',
        note: '',
        response: ''
    });

    useEffect(() => {
        if (!task) return;
        setFormData({
            status: task.status || 'Not Started',
            assigned_to: task.assigned_to || '',
            note: task.note || '',
            response: task.response || ''
        });
    }, [task]);

    if (!task) {
        return null;
    }

    const openModal = () => {
        setFormData({
            status: task.status || 'Not Started',
            assigned_to: task.assigned_to || '',
            note: task.note || '',
            response: task.response || ''
        });
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsSubmitting(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const fireConfetti = () => {
        const duration = 2000;
        const end = Date.now() + duration;
        const frame = () => {
            confetti({
                particleCount: 3,
                angle: 60,
                spread: 55,
                origin: { x: 0 },
            });
            confetti({
                particleCount: 3,
                angle: 120,
                spread: 55,
                origin: { x: 1 },
            });
            if (Date.now() < end) requestAnimationFrame(frame);
        };
        frame();
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isSubmitting) return;

        const statusChanged = formData.status !== (task.status || 'Not Started');
        const assigneeChanged = formData.assigned_to !== task.assigned_to;
        const trimmedNote = formData.note.trim();
        const noteChanged = trimmedNote !== (task.note || '');
        const responseChanged =
            formData.status === 'Completed' &&
            formData.response !== (task.response || '');

        if (!statusChanged && !assigneeChanged && !noteChanged && !responseChanged) {
            closeModal();
            return;
        }

        setIsSubmitting(true);

        const isRevertingFromCompleted =
            task.status === 'Completed' && formData.status !== 'Completed';
        const isNewlyCompleted =
            task.status !== 'Completed' && formData.status === 'Completed';

        const currentTasks = project.project_tasks || [];
        const updatedTasks = currentTasks.map((t) => {
            if (!isSameTask(t, task)) return t;

            const updatedTask = {
                ...t,
                status: formData.status,
                assigned_to: formData.assigned_to,
                updated_at: new Date().toISOString().split('T')[0]
            };

            if (trimmedNote) {
                updatedTask.note = trimmedNote;
            } else if (noteChanged) {
                updatedTask.note = '';
            }

            if (formData.status === 'Completed') {
                updatedTask.response = formData.response;
            }

            if (isRevertingFromCompleted) {
                updatedTask.force_design_change = true;
            }

            return updatedTask;
        });

        try {
            const updated_project = await updateData(
                token,
                `${base_url}/apis/projects/${project.id}`,
                { project_tasks: updatedTasks }
            );

            if (!updated_project.project) {
                window.showAlert(500, 'Failed to update task. Please try again.');
                return;
            }

            setProjects((prevProjects) =>
                prevProjects.map((p) =>
                    p.id === project.id ? updated_project.project : p
                )
            );
            onProjectUpdated?.(updated_project.project);

            const updatedTask = updatedTasks.find((t) =>
                t.task_name === task.task_name &&
                t.due_date === task.due_date &&
                t.assigned_to === formData.assigned_to
            );

            if (assigneeChanged && formData.assigned_to && updatedTask) {
                try {
                    await postData(
                        token,
                        `${base_url}/apis/alerts/new-task/${formData.assigned_to}`,
                        updatedTask
                    );
                } catch (notificationError) {
                    console.error('Error sending reassignment notification:', notificationError);
                }
            }

            if (isNewlyCompleted && task.created_by && updatedTask) {
                try {
                    await postData(
                        token,
                        `${base_url}/apis/alerts/task-completed/${task.created_by}/${project.id}`,
                        updatedTask
                    );
                } catch (notificationError) {
                    console.error('Error sending completion notification:', notificationError);
                }
                fireConfetti();
            }

            window.showAlert(200, 'Task updated successfully!');
            closeModal();
        } catch (error) {
            console.error('Error updating task:', error);
            if (error.status === 404) {
                window.showAlert(404, 'Task not found');
            } else if (error.status >= 400 && error.status < 500) {
                window.showAlert(error.status, 'Invalid task data. Please check your input.');
            } else {
                window.showAlert(500, 'Failed to update task. Please try again.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const editTaskForm = (
        <div>
            <h2 className="modal-title modal-title--compact">Edit Task</h2>
            <form onSubmit={handleSubmit} className="company-form-grid">
                <div className="form-row">
                    <div className="form-group">
                        <label className="form-label">Task</label>
                        <div className="form-readonly">
                            {task.task_name || 'N/A'}
                        </div>
                    </div>
                </div>

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="assigned_to" className="form-label">
                            Assigned To *
                        </label>
                        <select
                            id="assigned_to"
                            name="assigned_to"
                            value={formData.assigned_to}
                            onChange={handleChange}
                            className="form-select"
                            required
                        >
                            <option value="">Select team member</option>
                            {task.assigned_to && !TEAM.includes(task.assigned_to) && (
                                <option value={task.assigned_to}>{task.assigned_to}</option>
                            )}
                            {TEAM.map((email) => (
                                <option key={email} value={email}>
                                    {email}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label className="form-label">Assigned By</label>
                        <div className="form-readonly">
                            {task.created_by || 'Unknown'}
                        </div>
                    </div>

                    <div className="form-group">
                        <label htmlFor="status" className="form-label">
                            Status *
                        </label>
                        <select
                            id="status"
                            name="status"
                            value={formData.status}
                            onChange={handleChange}
                            className="form-select"
                            required
                        >
                            {TASK_STATUSES.map((status) => (
                                <option key={status} value={status}>
                                    {status}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>

                {task.task_description ? (
                    <div className="form-row">
                        <div className="form-group">
                            <label className="form-label">Description</label>
                            <div className="form-readonly form-readonly--multiline">
                                {task.task_description}
                            </div>
                        </div>
                    </div>
                ) : null}

                <div className="form-row">
                    <div className="form-group">
                        <label htmlFor="note" className="form-label">
                            Note for assignee
                        </label>
                        <textarea
                            id="note"
                            name="note"
                            value={formData.note}
                            onChange={handleChange}
                            rows="3"
                            className="form-textarea"
                            placeholder="Context or instructions for the person assigned to this task..."
                        />
                    </div>

                    {formData.status === 'Completed' && (
                        <div className="form-group">
                            <label htmlFor="response" className="form-label">
                                Completion response
                            </label>
                            <textarea
                                id="response"
                                name="response"
                                value={formData.response}
                                onChange={handleChange}
                                rows="3"
                                className="form-textarea"
                                placeholder="Enter your response or completion notes..."
                            />
                        </div>
                    )}
                </div>

                <div className="form-actions">
                    <button
                        type="button"
                        onClick={closeModal}
                        className="btn btn-secondary"
                        disabled={isSubmitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="btn btn-primary"
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <FontAwesomeIcon icon={faSpinner} spin /> Saving...
                            </>
                        ) : (
                            'Save Changes'
                        )}
                    </button>
                </div>
            </form>
        </div>
    );

    return (
        <div>
            <button
                type="button"
                onClick={openModal}
                className="btn btn-action"
                disabled={isSubmitting}
                title="Edit task"
            >
                <FontAwesomeIcon icon={faPen} /> Edit Task
            </button>
            {isModalOpen && (
                <Modal form={editTaskForm} setIsModalOpen={closeModal} isWide fitContent />
            )}
        </div>
    );
};
